import React, { useEffect, useState } from 'react';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Spin, Modal, DatePicker, Button, message, Tag } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom'; // Import useHistory hook from React Router
import PdfViewer from '../molecules/PdfViewer';
import ApproveSubmitForm from '../molecules/ApproveSubmitForm';
import {
    getInvoiceById,
    approveInvoice,
    updateInvoice,
    deleteInvoice,
    rejectInvoice,
    approveInvoiceFirstApprover,
    markInvoiceAsPaid
} from '../../api/InvoiceContainer';
import { useAuth } from '../../context/AuthContext';
import { moveFileFromOneDrive, deletePdfFromOneDrive, moveFileToRejectedFolder, moveFileToPaidFolder } from '../../api/PdfUpload';
import InvoiceForm from '../molecules/InvoiceForm';
import { getVendors, getCompanies } from '../../api/VendorsContainer';
import {
    ACCOUNTING_EMAIL_ROLE,
    FINANCIAL_CONTROLLER_ROLE,
    TREASURY_EMAIL_ROLE,
    getFinancialControllers,
} from '../../api/FinancialControllersContainer';
import { getUsers } from '../../api/UsersContainer';
import { controllerExistsInUsers, getControllerDisplayName } from '../../utils/userUtils';
import dayjs from 'dayjs';
import { folderVariables, folderVariablesSodaWorld } from '../../api/oneDriveConfig';
import { sendEmail, sendEmailApproval, sendEmailPaid } from '../../api/NodeServer';

// GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.1.392/pdf.worker.min.mjs`;

const InvoiceDetail = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [comments, setComments] = useState('');
    const [firstApproverComment, setFirstApproverComment] = useState('');
    const [sender, setSender] = useState('');
    const [senderEmail, setSenderEmail] = useState('');
    const [senderValue, setSenderValue] = useState('');
    const [status, setStatus] = useState('');
    const [sendingDate, setSendingDate] = useState('');
    const [approvalDate, setApprovalDate] = useState('');
    const [firstApproverApprovalDate, setFirstApproverApprovalDate] = useState('');
    const [firstApprover, setFirstApprover] = useState('');
    const [complianceCheckController, setComplianceCheckController] = useState('');
    const [complianceCheckComment, setComplianceCheckComment] = useState('');
    const [complianceCheckControllerApprovalDate, setComplianceCheckControllerApprovalDate] = useState('');
    const [fileName, setFileName] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const invoiceHistoryUrl = `/invoice-history${location.search || ''}`;
    const { accessToken, role, user } = useAuth();
    const [vendors, setVendors] = useState([]);
    const [vendor, setVendor] = useState('');
    const [company, setCompany] = useState('');
    const [companies, setCompanies] = useState([]);
    const [controllers, setControllers] = useState([]);
    const [invoiceDate, setInvoiceDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('');
    const [vendorId, setVendorId] = useState('');
    const [checkBoxValue, setCheckBoxValue] = useState(false);
    const [paidModalVisible, setPaidModalVisible] = useState(false);
    const [paidDate, setPaidDate] = useState(dayjs());

    const goBack = () => {
        navigate(invoiceHistoryUrl);
    };

    const showAlert = (type, message) => {
        Modal[type]({
            title: 'Alert',
            content: message,
        });
    };

    useEffect(() => {
        const fetchInvoice = async () => {
            setLoading(true);
            const invoice = await getInvoiceById(id);
            setSender(invoice.sender);
            setSenderEmail(invoice.sender_email);
            setSenderValue(invoice.sender_comment);
            setComments(invoice.approval_comment);
            setFirstApproverComment(invoice.first_approval_comment);
            setFirstApprover(invoice.first_approver);
            setComplianceCheckController(invoice.compliance_check_controller);
            setComplianceCheckComment(invoice.compliance_check_comment);
            setStatus(invoice.status);
            setFileName(invoice.file_name);
            const date = new Date(invoice.sender_date);
            const formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
            setSendingDate(formattedDate);
            if (invoice.approved_date) {
                const approvalDate = new Date(invoice.approved_date);
                const formattedApprovalDate = `${approvalDate.getDate()}/${approvalDate.getMonth() + 1}/${approvalDate.getFullYear()}`;
                setApprovalDate(formattedApprovalDate);
            }
            if (invoice.first_approved_date) {
                const approvalDate = new Date(invoice.first_approved_date);
                const formattedApprovalDate = `${approvalDate.getDate()}/${approvalDate.getMonth() + 1}/${approvalDate.getFullYear()}`;
                setFirstApproverApprovalDate(formattedApprovalDate);
            }
            if (invoice.compliance_check_controller_approved_date) {
                const approvalDate = new Date(invoice.compliance_check_controller_approved_date);
                const formattedApprovalDate = `${approvalDate.getDate()}/${approvalDate.getMonth() + 1}/${approvalDate.getFullYear()}`;
                setComplianceCheckControllerApprovalDate(formattedApprovalDate);
            }
            setCompany(invoice.company);
            setCheckBoxValue(invoice.agreement === 'Approved with Agreement' ? true : false);
            //console.log(invoice.agreement === 'Approved with Agreement' ? true : false)
            setLoading(false);
        };

        fetchInvoice();

        return () => {
            // Reset state when unmounting
            setComments('');
            setFirstApproverComment('');
            setSender('');
            setSenderValue('');
            setStatus('');
        };
    }, [id]);

    useEffect(() => {
        const fetchData = async () => {
            const data = await getInvoiceById(id);
            if (data.invoice_date) {
                setInvoiceDate(dayjs(data.invoice_date));
            }
            else {
                setInvoiceDate('');
            }

            if (data.due_date) {
                setDueDate(dayjs(data.due_date));
            }
            else {
                setDueDate('');
            }

            setInvoiceNumber(data.invoice_number);
            setAmount(data.amount);
            setCurrency(data.currency);
            setVendorId(data.vendor_id);
            setCompany(data.company);
        };

        fetchData();
    }, [id]);

    useEffect(() => {
        const fetchVendors = async () => {
            const data = await getVendors();
            setVendors(data);

            const vendor = data?.find((vendor) => vendor.id === vendorId);
            setVendor(vendor?.vendor);
        };

        fetchVendors();

    }, [vendorId]);

    useEffect(() => {
        const fetchCompanies = async () => {
            const data = await getCompanies();
            setCompanies(data);
        };

        const fetchControllers = async () => {
            const controllersData = await getFinancialControllers();
            setControllers(controllersData);
        };

        fetchCompanies();

        fetchControllers();

    }, [company]);

    const handleFormSubmit = async () => {
        setLoading(true);

        try {
            const configuredFinancialController = controllers.find((controller) => (
                controller.role === FINANCIAL_CONTROLLER_ROLE
            ));
            const configuredTreasuryEmail = controllers.find((controller) => (
                controller.role === TREASURY_EMAIL_ROLE
            ));
            const bccEmails = controllers
                .filter((controller) => controller.role === ACCOUNTING_EMAIL_ROLE)
                .map((controller) => controller.email)
                .filter(Boolean);
            const validateControllerUser = async (controller) => {
                if (!controller?.email) {
                    showAlert('error', `User ${getControllerDisplayName(controller)} is missing an email`);
                    setLoading(false);
                    return false;
                }

                const users = await getUsers();

                if (!controllerExistsInUsers(users, controller)) {
                    showAlert('error', `User ${getControllerDisplayName(controller)} does not exist`);
                    setLoading(false);
                    return false;
                }

                return true;
            };

            const validateFinancialController = async () => {
                if (!configuredFinancialController) {
                    showAlert('error', 'Please configure a Financial Controller before approving this invoice');
                    setLoading(false);
                    return false;
                }

                return validateControllerUser(configuredFinancialController);
            };

            const validateTreasuryEmail = async () => {
                if (!configuredTreasuryEmail) {
                    showAlert('error', 'Please configure a Treasury Email before approving this invoice');
                    setLoading(false);
                    return false;
                }

                return validateControllerUser(configuredTreasuryEmail);
            };

            if (status === 'Pending Compliance Check') {
                if (firstApproverComment === '' || firstApprover === '') {
                    if (!(await validateFinancialController())) {
                        return;
                    }

                    await approveInvoiceFirstApprover(id, user.name, firstApproverComment, complianceCheckController, complianceCheckComment, 'Pending');
                    await sendEmail(id, configuredFinancialController.email, user?.name, vendor, user?.username, bccEmails);
                    setLoading(false);
                    navigate(invoiceHistoryUrl);
                }
                else {
                    const firstApproverController = controllers.find(controller => controller.controller === firstApprover);

                    if (!firstApproverController) {
                        showAlert('error', 'The selected First Approver is not configured');
                        setLoading(false);
                        return;
                    }

                    if (!(await validateControllerUser(firstApproverController))) {
                        return;
                    }

                    await approveInvoiceFirstApprover(id, firstApprover, firstApproverComment, complianceCheckController, complianceCheckComment, 'Pending First Approval');
                    await sendEmail(id, firstApproverController.email, user?.name, vendor, user?.username, bccEmails);
                    setLoading(false);
                    navigate(invoiceHistoryUrl);
                }
            }
            else if (status === 'Pending First Approval') {
                if (!(await validateFinancialController())) {
                    return;
                }

                await approveInvoiceFirstApprover(id, user.name, firstApproverComment, '', '', 'Pending');
                await sendEmail(id, configuredFinancialController.email, user?.name, vendor, user?.username, bccEmails);
                setLoading(false);
                navigate(invoiceHistoryUrl);
            }
            else if (status === 'Pending' && (role === 'Admin' || role === 'Financial Controller')) {
                if (!(await validateTreasuryEmail())) {
                    return;
                }

                await moveFileFromOneDrive(accessToken, fileName, company);
                await approveInvoice(id, user.name, comments, 'Approved', checkBoxValue);

                const companyFolder = company === 'We Soda Ltd' ? folderVariables : company === 'Soda World Ltd' ? folderVariablesSodaWorld : folderVariables

                await deletePdfFromOneDrive(accessToken, companyFolder.invoiceFolder, fileName);
                await sendEmailApproval(id, configuredTreasuryEmail.email, user?.name, vendor, user?.username);
                setLoading(false);
                navigate(invoiceHistoryUrl);
            }
        } catch (error) {
            console.error('Error approving invoice:', error);
            message.error(error.message || 'Invoice could not be approved');
            setLoading(false);
        }
    }

    const handleVendorChange = (newVendor) => {
        setVendor(newVendor);
    };

    const handleCompanyChange = (newCompany) => {
        setCompany(newCompany);
    };

    const handleInvoiceNumberChange = (e) => {
        setInvoiceNumber(e.target.value);
    }

    const handleInvoiceDateChange = (date) => {
        setInvoiceDate(date);
    }

    const handleDueDateChange = (date) => {
        setDueDate(date);
    }

    const handleCurrencyChange = (value) => {
        setCurrency(value);
    };

    const handleAmountChange = (e) => {
        setAmount(e.target.value);
    }

    const handleSubmit = async () => {
        setLoading(true);
        // Update the invoice with the new values
        const invoiceData = {
            company: company,
            invoice_date: invoiceDate,
            due_date: dueDate,
            invoice_number: invoiceNumber,
            amount: amount,
            currency: currency,
            vendor_id: vendors.find((v) => v.vendor === vendor).id,
        };

        await updateInvoice(id, invoiceData);
        setLoading(false);

        message.success('Invoice updated successfully');
    };

    const handleDelete = async () => {
        setLoading(true);
        await deleteInvoice(id);
        // check status

        const companyFolder = company === 'We Soda Ltd' ? folderVariables : company === 'Soda World Ltd' ? folderVariablesSodaWorld : folderVariables
        const folder = status === 'Approved' ? companyFolder.invoiceApprovalFolder : status === 'Paid' ? companyFolder.invoicePaidFolder : companyFolder.invoiceFolder;

        await deletePdfFromOneDrive(accessToken, folder, fileName);
        setLoading(false);
        message.success('Invoice deleted successfully');
        navigate(invoiceHistoryUrl);
    };

    const handleCheckBoxChange = (e) => {
        setCheckBoxValue(e.target.checked);
    };

    const handleReject = async () => {
        setLoading(true);
        if (status === 'Pending First Approval') {
            rejectInvoice(id, user.name, firstApproverComment, 'Rejected')
        }
        else if (status === 'Pending Compliance Check') {
            rejectInvoice(id, user.name, complianceCheckComment, 'Rejected')
        }
        else if (status === 'Pending' && (role === 'Admin' || role === 'Financial Controller')) {
            rejectInvoice(id, user.name, comments, 'Rejected')
        }

        const companyFolder = company === 'We Soda Ltd' ? folderVariables : company === 'Soda World Ltd' ? folderVariablesSodaWorld : folderVariables
        const folder = status === 'Approved' ? companyFolder.invoiceApprovalFolder : companyFolder.invoiceFolder;

        await moveFileToRejectedFolder(accessToken, fileName, company, status)
            .then(() => {
                deletePdfFromOneDrive(accessToken, folder, fileName)
                    .then(() => {
                        setLoading(false);
                        navigate(invoiceHistoryUrl);
                    });
            });
    };

    const handleMarkAsPaid = async () => {
        setLoading(true);
        await markInvoiceAsPaid(id, user.name, 'Paid', paidDate)
            .then(() => {
                const companyFolder = company === 'We Soda Ltd' ? folderVariables : company === 'Soda World Ltd' ? folderVariablesSodaWorld : folderVariables
                const folder = status === 'Approved' ? companyFolder.invoiceApprovalFolder : companyFolder.invoiceFolder;

                moveFileToPaidFolder(accessToken, fileName, company, status);
                deletePdfFromOneDrive(accessToken, folder, fileName);

                sendEmailPaid(id, senderEmail, vendor, user?.username, user?.name);

                setLoading(false);
                navigate(invoiceHistoryUrl);
            });

    }

    const datePickerStyle = {
        width: "100%",
        height: "40px",
        fontSize: "1em",
        borderRadius: "5px",
        border: "1px solid #ccc",
        padding: "10px",
        boxSizing: "border-box",
        marginTop: "12px"
    };

    const tagColorHandler = (tag) => {
        switch (tag) {
            case 'Pending':
                return 'orange';
            case 'Approved':
                return 'green';
            case 'Rejected':
                return 'red';
            case 'Pending Compliance Check':
                return 'cyan';
            case 'Pending First Approval':
                return 'blue';
            case 'Paid':
                return 'purple';
            default:
                return 'default';
        }
    };
    const canShowWorkflow = ((status === 'Approved' || status === 'Paid') || ((status === 'Pending' || (status === 'Pending First Approval' || status === 'Pending Compliance Check')) &&
        (role === 'Admin' || role === 'Financial Controller' || role === 'First Approver' || role === 'Sender or Accountant' || role === 'First Approver & Compliance')));
    const canEditInvoice = role === 'Sender' && status === 'Pending';
    const canDeleteInvoice = role === 'Admin';

    if (sender === '' || loading) {
        return <Spin
            size="large"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flex: 1, marginTop: -100 }}
        />;
    }

    return (
        <div className="invoice-detail-page">
            <div className="invoice-detail-header">
                <div className="invoice-detail-title-group">
                    <button onClick={goBack} className="detail-back-button">
                        <FontAwesomeIcon icon={faArrowLeft} /> Back
                    </button>
                    <div>
                        <span className="page-kicker">Invoice detail</span>
                        <h1>{vendor || 'Invoice'}</h1>
                        <p>{company || 'Company'} · Invoice #{invoiceNumber || '-'}</p>
                    </div>
                </div>
                <div className="invoice-detail-header-actions">
                    <Tag color={tagColorHandler(status)} className="invoice-status-tag">
                        {status?.toUpperCase()}
                    </Tag>
                    {canDeleteInvoice && (
                        <Button type="primary" danger onClick={handleDelete}>
                            Delete
                        </Button>
                    )}
                </div>
            </div>

            <div className="invoice-detail-layout">
                <section className="invoice-preview-panel" aria-label="Invoice preview">
                    <PdfViewer accessToken={accessToken} fileName={fileName} status={status} company={company} />
                </section>
                <aside className="invoice-workflow-panel" aria-label="Invoice workflow">
                    {canShowWorkflow && (
                        <ApproveSubmitForm
                            sender={sender}
                            senderValue={senderValue}
                            status={status}
                            sendingDate={sendingDate}
                            approvalDate={approvalDate}
                            onSubmit={handleFormSubmit}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            complianceCheckController={complianceCheckController}
                            complianceCheckControllerCommentDate={complianceCheckControllerApprovalDate}
                            complianceCheckComment={complianceCheckComment}
                            setComplianceCheckComment={(e) => setComplianceCheckComment(e.target.value)}
                            firstApprover={firstApprover}
                            firstApproverCommentDate={firstApproverApprovalDate}
                            firstApproverComment={firstApproverComment}
                            setFirstApproverComment={(e) => setFirstApproverComment(e.target.value)}
                            checkBoxValue={checkBoxValue}
                            onCheckBoxChange={handleCheckBoxChange}
                            goBack={goBack}
                            role={role}
                            onReject={handleReject}
                            onPay={() => setPaidModalVisible(true)}
                        />
                    )}
                    {canEditInvoice && (
                        <InvoiceForm
                            company={company}
                            vendor={vendor}
                            invoiceDate={invoiceDate}
                            dueDate={dueDate}
                            invoiceNumber={invoiceNumber}
                            amount={amount}
                            currency={currency}
                            currencyOptions={['GBP', 'USD', 'EUR', 'TL', 'TRY']}
                            vendorOptions={vendors}
                            companyOptions={companies}
                            handleVendorChange={handleVendorChange}
                            handleCompanyChange={handleCompanyChange}
                            handleInvoiceNumberChange={handleInvoiceNumberChange}
                            handleInvoiceDateChange={handleInvoiceDateChange}
                            handleDueDateChange={handleDueDateChange}
                            handleCurrencyChange={handleCurrencyChange}
                            handleAmountChange={handleAmountChange}
                            handleSubmit={handleSubmit}
                            buttonText='Update'
                            marginLeft={'0'}
                        />
                    )}
                </aside>
                <Modal
                    title="Mark invoice as paid"
                    open={paidModalVisible}
                    onOk={() => { setPaidModalVisible(false); handleMarkAsPaid(); }}
                    onCancel={() => setPaidModalVisible(false)}
                >
                    <p className="modal-helper-copy">Select the payment date for this invoice.</p>
                    <DatePicker
                        id="paid-date"
                        value={paidDate}
                        onChange={(date) => setPaidDate(date)}
                        required
                        format="DD/MM/YYYY"
                        style={datePickerStyle}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default InvoiceDetail;
